import {
  toSalesCountFunnelData,
  toSalesValueFunnelData,
} from 'screens/Dashboard/Analytics/AnalyticsFunnelChart';
import {
  toOrderTypeData,
  toQuoteTypeData,
} from 'screens/Dashboard/Analytics/AnalyticsPieChart';

import {lshFormatCurrency, lshFormatInteger} from './analyticsData';
import {
  AnalyticsBreakdownRow,
  AnalyticsReportInput,
  AnalyticsTopProductRow,
} from './analyticsTypes';

function lshEscapeHtml(value: unknown) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function lshRenderMetricCards(
  metrics: Array<{label: string; value: string; detail?: string}>,
) {
  return `
    <div class="report-metrics">
      ${metrics
        .map(
          (metric) => `
            <div class="report-metric">
              <span>${lshEscapeHtml(metric.label)}</span>
              <strong>${lshEscapeHtml(metric.value)}</strong>
              ${
                metric.detail
                  ? `<small>${lshEscapeHtml(metric.detail)}</small>`
                  : ''
              }
            </div>
          `,
        )
        .join('')}
    </div>
  `;
}

function lshRenderFunnelReportTable(
  title: string,
  rows: Array<{name: string; valueFormatted: string | number}>,
) {
  return `
    <div class="report-card">
      <h3>${lshEscapeHtml(title)}</h3>
      <table>
        <tbody>
          ${rows
            .map(
              (row) => `
                <tr>
                  <td>${lshEscapeHtml(row.name)}</td>
                  <td class="number">${lshEscapeHtml(row.valueFormatted)}</td>
                </tr>
              `,
            )
            .join('')}
        </tbody>
      </table>
    </div>
  `;
}

function lshRenderBreakdownReportTable(
  title: string,
  rows: AnalyticsBreakdownRow[],
  valueLabel: string,
  valueFormatter: (value: number) => string,
) {
  return `
    <div class="report-card">
      <h3>${lshEscapeHtml(title)}</h3>
      ${
        rows.length
          ? `<table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Count</th>
                  <th>${lshEscapeHtml(valueLabel)}</th>
                  <th>Share</th>
                </tr>
              </thead>
              <tbody>
                ${rows
                  .map(
                    (row) => `
                      <tr>
                        <td>${lshEscapeHtml(row.label)}</td>
                        <td class="number">${lshFormatInteger(
                          row.count || 0,
                        )}</td>
                        <td class="number">${lshEscapeHtml(
                          valueFormatter(row.value || 0),
                        )}</td>
                        <td>
                          <div class="report-bar">
                            <span style="width:${Math.min(
                              row.percent || 0,
                              100,
                            )}%"></span>
                          </div>
                          <small>${lshFormatInteger(row.percent || 0)}%</small>
                        </td>
                      </tr>
                    `,
                  )
                  .join('')}
              </tbody>
            </table>`
          : '<p class="empty">No data available</p>'
      }
    </div>
  `;
}

function lshRenderTopProductsReportTable(
  title: string,
  rows: AnalyticsTopProductRow[],
) {
  return `
    <div class="report-card">
      <h3>${lshEscapeHtml(title)}</h3>
      ${
        rows.length
          ? `<table>
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Quantity</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                ${rows
                  .map(
                    (row) => `
                      <tr>
                        <td>${lshEscapeHtml(row.name)}</td>
                        <td class="number">${lshFormatInteger(
                          row.quantity,
                        )}</td>
                        <td class="number">${lshEscapeHtml(
                          lshFormatCurrency(row.value),
                        )}</td>
                      </tr>
                    `,
                  )
                  .join('')}
              </tbody>
            </table>`
          : '<p class="empty">No data available</p>'
      }
    </div>
  `;
}

function lshRenderSupplierEngagementReportTable(
  rows: Array<{name: string; numberOfViews: number}>,
) {
  return `
    <div class="report-card">
      <h3>Most viewed products</h3>
      ${
        rows.length
          ? `<table>
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Views</th>
                </tr>
              </thead>
              <tbody>
                ${rows
                  .map(
                    (row) => `
                      <tr>
                        <td>${lshEscapeHtml(row.name)}</td>
                        <td class="number">${lshFormatInteger(
                          row.numberOfViews || 0,
                        )}</td>
                      </tr>
                    `,
                  )
                  .join('')}
              </tbody>
            </table>`
          : '<p class="empty">No data available</p>'
      }
    </div>
  `;
}

function lshRenderReportSection(title: string, children: string) {
  return `
    <section class="report-section">
      <h2>${lshEscapeHtml(title)}</h2>
      ${children}
    </section>
  `;
}

function lshRenderSummarySection(input: AnalyticsReportInput) {
  return lshRenderReportSection(
    'Executive summary',
    lshRenderMetricCards([
      {
        label: 'Quotes',
        value: lshFormatInteger(input.stats.quotesCount),
        detail: lshFormatCurrency(input.stats.quotesTotal),
      },
      {
        label: 'Orders',
        value: lshFormatInteger(input.stats.invoicesCount),
        detail: lshFormatCurrency(input.stats.invoicesTotal),
      },
      {
        label: 'Paid orders',
        value: lshFormatInteger(input.stats.paidInvoicesCount),
        detail: `${input.paidOrderRate}% paid`,
      },
      {
        label: 'Conversion',
        value: `${input.salesConversion}%`,
        detail: 'Quotes to orders',
      },
      {
        label: 'Funding ask',
        value: lshFormatCurrency(input.totalFundingAsk),
        detail: `${lshFormatInteger(input.applications.length)} applications`,
      },
      {
        label: 'Programs',
        value: lshFormatInteger(input.programs.length),
        detail: `${lshFormatInteger(input.totalProgramHectares)} ha`,
      },
    ]),
  );
}

function lshRenderOrderQuoteSection(input: AnalyticsReportInput) {
  return lshRenderReportSection(
    'Order and quote analytics',
    `<div class="report-grid two">
      ${lshRenderFunnelReportTable(
        'Sales conversion by count',
        toSalesCountFunnelData(input.stats),
      )}
      ${lshRenderFunnelReportTable(
        'Sales conversion by value',
        toSalesValueFunnelData(input.stats),
      )}
      ${lshRenderFunnelReportTable(
        'Quote status',
        toQuoteTypeData(input.stats),
      )}
      ${lshRenderFunnelReportTable(
        'Order status',
        toOrderTypeData(input.stats),
      )}
    </div>`,
  );
}

function lshRenderFundingSection(input: AnalyticsReportInput) {
  return lshRenderReportSection(
    'Funding analytics',
    `${lshRenderMetricCards([
      {
        label: 'Applications',
        value: lshFormatInteger(input.applications.length),
        detail: `${input.averageApplicationCompletion}% avg complete`,
      },
      {
        label: 'Total ask',
        value: lshFormatCurrency(input.totalFundingAsk),
        detail: 'Selected period',
      },
      {
        label: 'Funding categories',
        value: lshFormatInteger(input.fundingCategoryRows.length),
        detail: 'Cost categories',
      },
    ])}
    <div class="report-grid two">
      ${lshRenderBreakdownReportTable(
        'Application status',
        input.fundingStatusRows,
        'Ask',
        lshFormatCurrency,
      )}
      ${lshRenderBreakdownReportTable(
        'Funding ask by category',
        input.fundingCategoryRows,
        'Ask',
        lshFormatCurrency,
      )}
    </div>`,
  );
}

function lshRenderProgramSection(input: AnalyticsReportInput) {
  return lshRenderReportSection(
    'Program analytics',
    `${lshRenderMetricCards([
      {
        label: 'Programs',
        value: lshFormatInteger(input.programs.length),
        detail: 'Selected period',
      },
      {
        label: 'Hectares',
        value: lshFormatInteger(input.totalProgramHectares),
        detail: 'Program hectares',
      },
      {
        label: 'Products in programs',
        value: lshFormatInteger(
          input.programs.reduce(
            (total, program) => total + Number(program.lines?.length || 0),
            0,
          ),
        ),
        detail: 'Program lines',
      },
    ])}
    <div class="report-grid two">
      ${lshRenderBreakdownReportTable(
        'Program status',
        input.programStatusRows,
        'Hectares',
        lshFormatInteger,
      )}
      ${lshRenderBreakdownReportTable(
        'Program type',
        input.programTypeRows,
        'Hectares',
        lshFormatInteger,
      )}
    </div>`,
  );
}

function lshRenderProductActivitySection(input: AnalyticsReportInput) {
  return lshRenderReportSection(
    'Product activity from quotes and orders',
    `<div class="report-grid two">
      ${lshRenderTopProductsReportTable(
        'Top quoted products',
        input.topQuoteProducts,
      )}
      ${lshRenderTopProductsReportTable(
        'Top ordered products',
        input.topOrderProducts,
      )}
    </div>`,
  );
}

function lshRenderSupplierEngagementSection(input: AnalyticsReportInput) {
  return lshRenderReportSection(
    'Supplier engagement',
    lshRenderSupplierEngagementReportTable(input.supplierEngagementRows),
  );
}

function lshRenderReportStyles() {
  return `
    @page { size: A4; margin: 16mm; }
    * { box-sizing: border-box; }
    body {
      margin: 0;
      color: #143c3b;
      font-family: Inter, Arial, sans-serif;
      background: #fff;
    }
    .report {
      width: 100%;
      padding-bottom: 42px;
    }
    .report-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 24px;
      padding-bottom: 18px;
      border-bottom: 1px solid #dfe8e5;
    }
    .report-brand {
      display: flex;
      align-items: center;
      gap: 14px;
      min-width: 0;
    }
    .company-logo {
      width: 54px;
      height: 54px;
      object-fit: contain;
      border: 1px solid #e4ece9;
      border-radius: 10px;
      padding: 6px;
    }
    .company-logo-fallback {
      display: flex;
      align-items: center;
      justify-content: center;
      color: #267373;
      font-size: 24px;
      font-weight: 800;
    }
    h1, h2, h3, p { margin: 0; }
    h1 {
      color: #102f2e;
      font-size: 26px;
      line-height: 1.15;
    }
    .report-company {
      margin-top: 4px;
      color: #687672;
      font-size: 12px;
      font-weight: 700;
      text-transform: uppercase;
    }
    .report-meta {
      color: #687672;
      font-size: 12px;
      line-height: 1.55;
      text-align: right;
    }
    .report-section {
      margin-top: 26px;
      break-inside: avoid;
      page-break-inside: avoid;
    }
    .report-section h2 {
      margin-bottom: 12px;
      color: #102f2e;
      font-size: 18px;
      line-height: 1.2;
    }
    .report-metrics {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 10px;
    }
    .report-metric,
    .report-card {
      border: 1px solid #e1e9e6;
      border-radius: 8px;
      background: #fff;
    }
    .report-metric {
      padding: 12px;
    }
    .report-metric span {
      display: block;
      color: #687672;
      font-size: 10px;
      font-weight: 800;
      letter-spacing: .03em;
      text-transform: uppercase;
    }
    .report-metric strong {
      display: block;
      margin-top: 6px;
      color: #0c3f3d;
      font-size: 21px;
      line-height: 1.1;
    }
    .report-metric small {
      display: block;
      margin-top: 4px;
      color: #687672;
      font-size: 11px;
      font-weight: 700;
    }
    .report-grid {
      display: grid;
      gap: 12px;
    }
    .report-metrics + .report-grid {
      margin-top: 12px;
    }
    .report-grid.two {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
    .report-card {
      min-width: 0;
      padding: 12px;
    }
    .report-card h3 {
      margin-bottom: 10px;
      color: #102f2e;
      font-size: 13px;
      line-height: 1.2;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      font-size: 11px;
    }
    th {
      color: #697772;
      font-size: 9px;
      letter-spacing: .03em;
      text-align: left;
      text-transform: uppercase;
    }
    th, td {
      padding: 8px 6px;
      border-bottom: 1px solid #eef3f1;
      vertical-align: top;
    }
    td.number,
    th.number {
      text-align: right;
      white-space: nowrap;
    }
    .report-bar {
      width: 74px;
      height: 5px;
      overflow: hidden;
      border-radius: 99px;
      background: #e7eeeb;
    }
    .report-bar span {
      display: block;
      height: 100%;
      border-radius: inherit;
      background: #4fa574;
    }
    .empty {
      color: #687672;
      font-size: 12px;
    }
    .report-footer {
      position: fixed;
      right: 0;
      bottom: 0;
      display: flex;
      align-items: center;
      gap: 8px;
      color: #687672;
      font-size: 10px;
      font-weight: 700;
    }
    .report-footer img {
      width: 34px;
      height: 34px;
      object-fit: contain;
    }
    @media print {
      .report-section { break-inside: avoid; }
    }
  `;
}

export function lshBuildAnalyticsReportHtml(input: AnalyticsReportInput) {
  const sections: string[] = [];
  const companyLogo = input.companyLogo
    ? `<img class="company-logo" src="${lshEscapeHtml(
        input.companyLogo,
      )}" alt="" />`
    : `<div class="company-logo company-logo-fallback">${lshEscapeHtml(
        input.companyName.charAt(0) || 'K',
      )}</div>`;

  if (input.selectedSections.includes('summary')) {
    sections.push(lshRenderSummarySection(input));
  }

  if (input.selectedSections.includes('orderQuotes')) {
    sections.push(lshRenderOrderQuoteSection(input));
  }

  if (input.selectedSections.includes('funding')) {
    sections.push(lshRenderFundingSection(input));
  }

  if (input.selectedSections.includes('programs')) {
    sections.push(lshRenderProgramSection(input));
  }

  if (input.selectedSections.includes('productActivity')) {
    sections.push(lshRenderProductActivitySection(input));
  }

  if (input.selectedSections.includes('supplierEngagement')) {
    sections.push(lshRenderSupplierEngagementSection(input));
  }

  return `
    <!doctype html>
    <html>
      <head>
        <title>${lshEscapeHtml(input.companyName)} analytics report</title>
        <style>${lshRenderReportStyles()}</style>
      </head>
      <body>
        <main class="report">
          <header class="report-header">
            <div class="report-brand">
              ${companyLogo}
              <div>
                <h1>Analytics report</h1>
                <p class="report-company">${lshEscapeHtml(
                  input.companyName,
                )}</p>
              </div>
            </div>
            <div class="report-meta">
              <div>${lshEscapeHtml(input.rangeLabel)}</div>
              <div>Generated ${lshEscapeHtml(
                input.generatedAt.format('D MMM YYYY, HH:mm'),
              )}</div>
            </div>
          </header>
          ${sections.join('')}
        </main>
        <footer class="report-footer">
          <span>Generated with Khula app</span>
          <img src="${lshEscapeHtml(input.khulaLogo)}" alt="" />
        </footer>
      </body>
    </html>
  `;
}
